import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { QUERY_KEYS } from 'constants/constants'
import { usePartnerContext, useUserContext } from '../context'
import {
  collectSpaceCareerIds,
  resolveCareerStates,
  toCareerLookup
} from '../features/pricing/archived-careers'
import { query } from '../graphql'
import { talentsQueryOptions } from './use-talents-query'

const selectCareers = (response) => response?.[1]

// Welche Stellen der übergebenen Talente archiviert sind (D16, jeder Modus).
// Quellen sind der Cache der Talentliste (careerByPartnerByDate filtert nur
// status ACTIVE, archivierte Stellen sind also drin) und je Stelle der Cache
// von getCareer, den die Detailseite lädt. Je Stelle gilt der neueste Stand.
//
// Ohne fetchWhen liest der Hook nur den Cache und schickt keinen Request.
// Mit fetchWhen lädt er Stellen nach, die keine Quelle kennt, z. B. im
// Neueinladen-Dialog aus Discover. Bis dahin ist pending true. Unbekannte
// Stellen gelten als nicht archiviert, wie bisher.
export const useArchivedCareerIds = ({ spaces, fetchWhen = false }) => {
  const { partner } = usePartnerContext()
  const { cognitoUser } = useUserContext()
  const queryClient = useQueryClient()

  const careerIdsKey = JSON.stringify(collectSpaceCareerIds(spaces))
  const careerIds = useMemo(() => JSON.parse(careerIdsKey), [careerIdsKey])

  const list = useQuery({
    ...talentsQueryOptions({
      partnerId: partner?.id,
      username: cognitoUser?.username,
      queryClient
    }),
    enabled: false,
    select: selectCareers
  })
  const listLookup = useMemo(() => toCareerLookup(list.data), [list.data])

  const byId = useQueries({
    queries: careerIds.map((id) => ({
      queryKey: QUERY_KEYS.careerById(id),
      queryFn: () => query({ query: 'getCareer', variables: { id } }),
      enabled: !!fetchWhen && !listLookup(id)
    }))
  })

  const { archived, unknown } = resolveCareerStates(careerIds, [
    { careers: listLookup, updatedAt: list.dataUpdatedAt },
    ...byId.map(({ data, dataUpdatedAt }) => ({
      careers: data,
      updatedAt: dataUpdatedAt
    }))
  ])

  const archivedKey = JSON.stringify(archived)
  const archivedCareerIds = useMemo(
    () => new Set(JSON.parse(archivedKey)),
    [archivedKey]
  )

  const pending =
    !!fetchWhen &&
    unknown.length > 0 &&
    byId.some(({ fetchStatus }) => fetchStatus === 'fetching')

  return { archivedCareerIds, pending }
}
